/**
 * ErrorBoundary Component
 * Catches JavaScript errors in child components and displays a fallback UI
 * Integrates with Sentry for error reporting
 */

import React, { Component } from 'react';
import { captureException } from '../utils/sentry';
import { isStaleBuildError, reloadOnceForStaleBuild, staleBuildReloadInFlight } from '../utils/staleBuild';
import ServerError from '../pages/ServerError';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      // A reload onto the current build is on its way. Show a quiet notice
      // instead of the error screen and report nothing — see below.
      reloading: false
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Auto-recover from a tab running an older build (see utils/staleBuild.js)
    if (isStaleBuildError(error) && reloadOnceForStaleBuild()) {
      this.setState({ reloading: true });
      return;
    }

    // A reload is already on its way for an earlier error in this document.
    // `location.reload()` is asynchronous, so React carries on rendering in
    // the meantime and whatever it throws next belongs to the same failure —
    // including errors that look nothing like a missing chunk, such as
    // React.lazy reading `.default` off a module that never loaded. Reporting
    // these buried the real cause: they named React, not the deploy, and they
    // outnumbered every genuine error in Sentry.
    if (staleBuildReloadInFlight()) {
      this.setState({ reloading: true });
      return;
    }

    // Store error info for display
    this.setState({ errorInfo });

    // Report to Sentry
    captureException(error, {
      extra: {
        componentStack: errorInfo?.componentStack
      }
    });
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      reloading: false
    });
  };

  render() {
    // Checked BEFORE hasError, and consulting the module flag as well as our
    // own state: React calls getDerivedStateFromError and renders the
    // fallback BEFORE componentDidCatch runs, so without the flag the error
    // screen flashed on every successful recovery.
    if (this.state.reloading || (this.state.hasError && staleBuildReloadInFlight())) {
      return (
        <div
          role="status"
          aria-live="polite"
          style={{ padding: '2rem', textAlign: 'center', opacity: 0.7 }}
        >
          Updating to the latest version…
        </div>
      );
    }

    if (this.state.hasError) {
      // Check if a custom fallback was provided
      if (this.props.fallback) {
        // Support render-prop fallback: fallback={(error, resetError) => <MyFallback />}
        if (typeof this.props.fallback === 'function') {
          return this.props.fallback(this.state.error, this.resetError);
        }
        return this.props.fallback;
      }

      // Use the ServerError page as default fallback
      return (
        <ServerError
          error={this.state.error}
          resetError={this.resetError}
        />
      );
    }

    return this.props.children;
  }
}

// HOC for wrapping components with error boundary
export const withErrorBoundary = (WrappedComponent, fallback = null) => {
  return function WithErrorBoundary(props) {
    return (
      <ErrorBoundary fallback={fallback}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
};

// Hook-friendly error boundary wrapper
export const ErrorBoundaryProvider = ({ children, fallback }) => {
  return (
    <ErrorBoundary fallback={fallback}>
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBoundary;
